
import { computed, defineComponent, onMounted } from "vue";
import { useRoute } from "vue-router";

import {
  ColumnDefinition,
  BaseTable,
  BaseButton,
  EditIcon,
  DeleteIcon,
  NorthIcon,
  SouthIcon,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";
import {
  Nullable,
  useResource,
  useResourceDelete,
  useResourceMove,
} from "@tager/admin-services";

import { TermLinkType, TermType } from "@/typings/model";
import {
  deleteTermLink,
  getTermLinkList,
  moveTermLink,
} from "@/services/requests";
import useTermList from "@/hooks/useTermList";
import {
  getTermLinkCreateFormUrl,
  getTermLinkUpdateFormUrl,
} from "@/utils/paths";

const COLUMN_DEFS: Array<ColumnDefinition<TermLinkType>> = [
  {
    id: 1,
    name: "Название",
    field: "label",
  },
  {
    id: 2,
    name: "Ссылка",
    field: "url",
  },
  {
    id: 3,
    name: "Actions",
    field: "actions",
    style: { width: "180px", textAlign: "center", whiteSpace: "nowrap" },
    headStyle: { width: "180px", textAlign: "center" },
  },
];

export default defineComponent({
  name: "TermLinkList",
  components: {
    Page,
    BaseTable,
    BaseButton,
    EditIcon,
    DeleteIcon,
    NorthIcon,
    SouthIcon,
  },
  setup() {
    const route = useRoute();

    const termId = route.params.termId as string;

    const { data: termList, loading: isTermLoading } = useTermList();

    const selectedTerm = computed<Nullable<TermType>>(
      () =>
        termList.value.find((term) => String(term.id) === String(termId)) ??
        null
    );

    const pageTitle = computed<string>(() => {
      return selectedTerm.value
        ? `Ссылки слова "${selectedTerm.value.term}"`
        : "Ссылки слова";
    });

    const [
      fetchTermLinkList,
      { data: TermLinkList, loading: isTermLinkListLoading, error },
    ] = useResource<Array<TermLinkType>>({
      fetchResource: () => getTermLinkList(termId),
      initialValue: [],
      resourceName: "TermLink list",
    });

    onMounted(() => {
      fetchTermLinkList();
    });

    const { handleResourceDelete: handleTermLinkDelete, isDeleting } =
      useResourceDelete({
        deleteResource: deleteTermLink,
        resourceName: "TermLink",
        onSuccess: fetchTermLinkList,
      });

    const { handleResourceMove: handleTermLinkMove, isMoving } =
      useResourceMove({
        moveResource: moveTermLink,
        resourceName: "TermLink",
        onSuccess: fetchTermLinkList,
      });

    const isRowDataLoading = computed<boolean>(
      () => isTermLinkListLoading.value || isTermLoading.value
    );

    function isBusy(termId: number): boolean {
      return isDeleting(termId) || isMoving(termId) || isRowDataLoading.value;
    }

    return {
      columnDefs: COLUMN_DEFS,
      rowData: TermLinkList,
      isRowDataLoading,
      errorMessage: error,
      isBusy,
      pageTitle,
      getTermLinkUpdateFormUrl,
      getTermLinkCreateFormUrl,
      handleTermLinkDelete,
      termId,
      handleTermLinkMove,
    };
  },
});
